import React from 'react';

import { Accordion } from '@clutter/clean';

import { wt } from '@portal/initializers/wt';
import { PricingSetPriceEntryFragment } from '@portal/schema';

import { AccordionLabel } from '../accordion_label';
import { Options } from '../options';
import {
  accordionMetadata,
  PLAN_UPDATE_PAGE_NAME,
  protectionClassificationMetadata,
  protectionValue,
  protectionChanged,
  protectionTitle,
  protectionPlanSlug,
  PROTECTION_PLAN_SLUG_1000_INCLUDED,
  PROTECTION_PLAN_SLUG_2500,
  PROTECTION_PLAN_SLUG_MINIMAL_COVERAGE,
} from '../util';
import { Option } from './option';

export const Selector: React.FC<{
  orderID: string;
  loading: boolean;
  initialPriceEntry?: PricingSetPriceEntryFragment;
  selectedPriceEntry?: PricingSetPriceEntryFragment;
  options: PricingSetPriceEntryFragment[];
  setSelectedPriceEntry(entry?: PricingSetPriceEntryFragment): void;
  twoTierProtectionPlanCatalog?: boolean;
}> = ({
  orderID,
  loading,
  initialPriceEntry,
  selectedPriceEntry,
  options,
  setSelectedPriceEntry,
  twoTierProtectionPlanCatalog = false,
}) => {
  const includedEntry = options.find((o) => protectionPlanSlug(o) === PROTECTION_PLAN_SLUG_1000_INCLUDED);

  const classicMinimalCoverage =
    options.find((o) => protectionPlanSlug(o) === PROTECTION_PLAN_SLUG_MINIMAL_COVERAGE) ??
    options.find((o) => o.price.amount === 0);

  const selectableOptions = twoTierProtectionPlanCatalog
    ? options
        .filter((o) => {
          const slug = protectionPlanSlug(o);
          return slug === PROTECTION_PLAN_SLUG_1000_INCLUDED || slug === PROTECTION_PLAN_SLUG_2500;
        })
        .sort((a, b) => a.price.amount - b.price.amount)
    : options.filter((option) => option.price.amount > 0).sort((a, b) => a.price.amount - b.price.amount);

  /** Prefer included tier when present so classic "Remove" does not jump past it to minimal_coverage. */
  const minimalCoverage = includedEntry ?? classicMinimalCoverage;

  const onToggle = (isOpen: boolean) => {
    const params = {
      ...accordionMetadata(isOpen),
      container: 'item_protection_accordion',
      order_id: orderID,
    };
    wt.track(params);
  };

  const triggerWTEvent = (currentSelection: string, label: string) => {
    const params = {
      container: 'item_protection_accordion',
      previous_selection: protectionClassificationMetadata(selectedPriceEntry),
      current_selection: currentSelection,
      action: 'click',
      label: label,
      pageName: PLAN_UPDATE_PAGE_NAME,
      objectType: 'card',
      objectName: 'item_protection',
      order_id: orderID,
    };
    wt.track(params);
  };

  const setMinimalCoverage = () => {
    triggerWTEvent('NONE', 'Remove');
    setSelectedPriceEntry(minimalCoverage);
  };

  const handleTwoTierRemove = () => {
    if (!includedEntry) {
      return;
    }
    triggerWTEvent(protectionClassificationMetadata(includedEntry), 'Remove');
    setSelectedPriceEntry(includedEntry);
  };

  const onSelect = (currentPriceEntry: PricingSetPriceEntryFragment, label: string) => {
    const currentSelection = protectionClassificationMetadata(currentPriceEntry);
    triggerWTEvent(currentSelection, label);
    setSelectedPriceEntry(currentPriceEntry);
  };

  return (
    <Accordion.Item
      name="protection"
      label={
        <AccordionLabel
          loading={loading}
          title={protectionTitle(selectedPriceEntry)}
          value={protectionValue(selectedPriceEntry)}
          changed={protectionChanged(selectedPriceEntry, initialPriceEntry)}
        />
      }
      onToggle={onToggle}
    >
      <Options.Container>
        {selectableOptions.map((option) => (
          <Option
            key={option.id}
            option={option}
            selected={selectedPriceEntry?.id === option.id}
            onSelect={(label) => onSelect(option, label)}
            onRemove={
              twoTierProtectionPlanCatalog
                ? protectionPlanSlug(option) === PROTECTION_PLAN_SLUG_2500 && includedEntry
                  ? handleTwoTierRemove
                  : undefined
                : setMinimalCoverage
            }
          />
        ))}
      </Options.Container>
    </Accordion.Item>
  );
};
